import { audio } from '../systems/audio/AudioSystem.js';
import { buildSpoilsPool, rollChestSize, chestTier } from '../data/spoils.js';
import { weightedSample } from '../data/levelUpPool.js';

/**
 * The Spoils chest overlay (BULLET-HEAVEN.md §H3). Opens when the player walks
 * over the chest a dead boss dropped (entities/SpoilsChestDrop.js, spawned by
 * Game.js's _dropSpoilsChest) and reveals 1–5 rewards one at a time on a
 * slot-machine cadence.
 *
 * Follows LevelUpPicker's resolve-to-unpause contract exactly: it emits
 * `xp:levelup:open`-equivalents of its own (`spoils:opened` / `spoils:closed`,
 * which Game.js wires to the same `_autoPause` flag), so the simulation is
 * frozen for the whole reveal and resumes on exactly one event, whichever way
 * the chest is dismissed.
 *
 * The rewards are APPLIED UP FRONT, at open time, not as each one is revealed.
 * The reveal is presentation over a decision already made — which is what a
 * slot machine is. It also means a player who skips the animation, alt-tabs,
 * or is dismissed by a run ending mid-reveal has already received everything;
 * there is no path where the pause resolves and a reward is silently lost.
 */
/** Pause between the lid bursting and the first card landing. */
const LID_LEAD_IN_MS = 200;

export class SpoilsChest {
  constructor(eventBus, root, { getContext, save, rng = Math.random }) {
    this.eventBus = eventBus;
    this.root = root;
    this.getContext = getContext;
    this.save = save;
    // The run's spoils stream on a seeded run (BULLET-HELL.md §D8): both the
    // chest's size and which rewards it pays. Kept separate from the level-up
    // stream even though both draw through weightedSample — chests open on boss
    // kills and level-ups on XP thresholds, so one shared cursor would let the
    // order the two happened to interleave in decide what each paid.
    this.rng = rng;
    this.el = null;
    // 0-based count across the run — drives the hardcoded generous opening
    // (see data/spoils.js OPENING_CHEST_ROLLS).
    this.chestsOpened = 0;
    this._timers = [];

    this._onKeyDown = (e) => {
      // Any of the usual "get on with it" keys. Repeat players open four of
      // these a run and should never be taxed the full animation.
      if (['Space', 'Enter', 'Escape', 'KeyP'].includes(e.code)) {
        e.preventDefault();
        this._revealAll();
      }
    };

    eventBus.on('run:ended', () => this._close());
  }

  /** True while the overlay is up. Game.js's chest queue gates on this. */
  get isOpen() {
    return this.el !== null;
  }

  /**
   * @param {object} opts
   * @param {number} opts.stageIndex - gauntlet index, for chest quality.
   */
  open({ stageIndex = 0 } = {}) {
    if (this.el) return; // already open — two chests must never stack overlays
    const context = this.getContext();
    const tier = chestTier(stageIndex);
    const pool = buildSpoilsPool({ ...context, tier });
    if (pool.length === 0) return;

    const size = rollChestSize(this.chestsOpened, this.rng);
    this.chestsOpened++;
    // weightedSample draws without replacement, so a chest never pays the same
    // reward twice; a size larger than the pool simply pays the whole pool.
    const rewards = weightedSample(pool, size, false, this.rng);
    for (const reward of rewards) reward.apply();

    this.eventBus.emit('spoils:opened', { tier, count: rewards.length, stageIndex });
    this._render(rewards, tier);
  }

  _render(rewards, tier) {
    window.addEventListener('keydown', this._onKeyDown);

    const el = document.createElement('div');
    el.className = 'levelup-overlay spoils-overlay';
    // reduceFlash drops the reel's luminance only: no burst ring, no per-card
    // flare. Those are the flash that setting exists to turn off; a card
    // arriving after a card is not — that's skipAnimations' business.
    if (this.save?.reduceFlash) el.classList.add('spoils-no-flare');

    const title = document.createElement('h2');
    title.className = `levelup-title spoils-title spoils-title-${tier}`;
    title.textContent = 'Spoils';
    el.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'levelup-subtitle';
    subtitle.textContent = `${rewards.length} ${rewards.length === 1 ? 'reward' : 'rewards'}`;
    el.appendChild(subtitle);

    const cards = document.createElement('div');
    cards.className = 'levelup-cards spoils-cards';
    this._cards = rewards.map((reward) => {
      const card = document.createElement('div');
      card.className = `levelup-card spoils-card levelup-card-${reward.rarity ?? 'common'}`;
      card.innerHTML = `
        <div class="levelup-card-rarity">${(reward.rarity ?? 'common').toUpperCase()}</div>
        <div class="levelup-card-label">${reward.label}</div>
        <div class="levelup-card-desc">${reward.detail ?? ''}</div>
      `;
      cards.appendChild(card);
      return card;
    });
    el.appendChild(cards);

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'menu-button menu-button-primary spoils-dismiss';
    dismiss.textContent = 'Continue';
    dismiss.addEventListener('click', () => {
      audio.playSfx('click');
      this._close();
    });
    el.appendChild(dismiss);

    const hint = document.createElement('div');
    hint.className = 'pause-hint';
    hint.textContent = 'Space / Enter to reveal · Continue to fight';
    el.appendChild(hint);

    this.root.appendChild(el);
    this.el = el;

    this._startReel(tier);
  }

  /**
   * The reel. A lid beat first — a burst ring out of the middle of the overlay
   * in the chest's tier colour, on `spoilsOpen` — and then the cards land one
   * at a time on an accelerating cadence with a pitch that climbs per card.
   * The last of five arrives fastest and highest, which is the shape that makes
   * five feel like more than 5×1.
   *
   * Two independent opt-outs, and the split matters here more than anywhere:
   * this reel used to be skipped wholesale under `reduceEffects`, which
   * DEFAULTS ON — so the whole feature was invisible to a default save.
   *
   *   - `reduceFlash` drops the light (the burst here, the flare in CSS via
   *     `spoils-no-flare`) and keeps every card and every beat of timing.
   *   - `skipAnimations` drops the timing: cards land at once. It defaults
   *     OFF, so the reel still plays for a player who has said nothing.
   */
  _startReel(tier = 'plain') {
    const noFlare = !!this.save?.reduceFlash;
    const skipAnim = !!this.save?.skipAnimations;

    // Nothing to stagger — put every card up now and settle. Still fires the
    // audio, which is feedback rather than animation.
    if (skipAnim) {
      for (const card of this._cards) card.classList.add('spoils-card-revealed');
      if (!noFlare) this._timers.push(setTimeout(() => audio.playSfx('spoilsOpen'), 16));
      this._timers.push(setTimeout(() => audio.playSfx('spoilsSettle'), 160));
      return;
    }

    // The lid. The lead-in is what gives the first card something to arrive
    // *after*; without it the reel opened on a card already in flight.
    let delay = noFlare ? 0 : LID_LEAD_IN_MS;
    if (!noFlare) {
      const burst = document.createElement('div');
      burst.className = `spoils-burst spoils-burst-${tier}`;
      this.el.appendChild(burst);
      // Next frame, so the element is laid out unanimated first and the
      // keyframes actually run from their 0% state.
      this._timers.push(setTimeout(() => {
        burst.classList.add('spoils-burst-fire');
        audio.playSfx('spoilsOpen');
      }, 16));
    }

    this._cards.forEach((card, i) => {
      delay += Math.max(140, 340 - i * 50);
      this._timers.push(setTimeout(() => {
        card.classList.add('spoils-card-revealed');
        audio.playSfx('spoilsTick', { pitchScale: 1 + i * 0.12 });
      }, delay));
    });
    this._timers.push(setTimeout(() => audio.playSfx('spoilsSettle'), delay + 160));
  }

  /** Skip to the end — every card visible, no further timers. */
  _revealAll() {
    this._clearTimers();
    // The lid goes with the reel it belonged to — a ring expanding over cards
    // that are already all up reads as a glitch, not a reveal.
    this.el?.querySelector('.spoils-burst')?.remove();
    for (const card of this._cards ?? []) card.classList.add('spoils-card-revealed');
  }

  _clearTimers() {
    for (const timer of this._timers) clearTimeout(timer);
    this._timers = [];
  }

  _close() {
    if (!this.el) return;
    this._clearTimers();
    window.removeEventListener('keydown', this._onKeyDown);
    this.el.remove();
    this.el = null;
    this._cards = null;
    // The one event that unpauses. Emitted on every dismissal path, including
    // the run-ending one, so the sim can never be left frozen behind a chest.
    this.eventBus.emit('spoils:closed', {});
  }

  destroy() {
    this._clearTimers();
    window.removeEventListener('keydown', this._onKeyDown);
    this.el?.remove();
    this.el = null;
  }
}
